import Icon from '@passkeys/ui/icon';
import { useMemo, useState } from 'react';

import type { CombinedAssetWithBalance } from '@/types';
import usePrice from '@/ui/hooks/use-price';
import { cn } from '@/ui/utils/classnames';
import BaseItem from './base-item.js';
import SingleItem from './single-item.js';

type CombinedItemProps = {
  asset: CombinedAssetWithBalance;
};

function CombinedItem({ asset: combinableAsset }: CombinedItemProps) {
  const [expanded, setExpanded] = useState(false);

  const mainAsset = useMemo(
    () => combinableAsset.combinedAssets[0],
    [combinableAsset],
  );

  const { loading, value: price } = usePrice(mainAsset.ticker);

  return (
    <div className="flex w-full flex-col">
      <BaseItem
        name={mainAsset.name}
        displayName={combinableAsset.displayName}
        itemName={
          <>
            {combinableAsset.displayName}
            <Icon
              name="triangle-down"
              size={16}
              className={cn(
                'ml-[2px] transition-transform duration-200',
                expanded && 'rotate-180',
              )}
            />
          </>
        }
        displayTicker={combinableAsset.displayTicker}
        balance={combinableAsset.balance.toDefaultNumber()}
        fiatValue={combinableAsset.fiatValue.toDefaultNumber()}
        price={price}
        loading={loading}
        className="gap-x-4"
        dataTestId="combined-token-item"
        onClick={() => setExpanded((previous) => !previous)}
        stackedIcon
      />

      {expanded && (
        <ul className="ml-14 flex flex-col divide-y divide-solid divide-black/6 border-t border-solid border-black/6 theme-dark:divide-none theme-dark:border-none">
          {combinableAsset.combinedAssets.map((asset) => (
            <li key={asset.name} className="w-full">
              <SingleItem
                asset={asset}
                displayName={combinableAsset.displayName} // Use unified combined asset name for all assets.
                className="gap-x-3"
                withChain
                dataTestId="combined-single-token-item"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CombinedItem;
